/**
 * Pure logic for the "Open a conversation at: oldest unread" preference.
 * The anchor is snapshotted when a chat is opened so the first-unread target
 * stays put while read receipts and realtime messages land underneath it.
 */

import type { Chat, Message } from '../../types/messaging';
import type { OpenConversationAt } from '../preferences/preferencesSchema';

export interface UnreadAnchor {
  chatId: string;
  /** The reader's chat_participants.last_read_at at open, when the list route supplies it. */
  lastReadAt: string | null;
  /** Server unread count at open (forced to at least 1 for a chat marked unread). */
  unreadCount: number;
  /** Newest message id the chat list knew about at open; tells us the loaded page is current. */
  lastMessageId: string | null;
}

/**
 * `frozen` is false only while the open chat's record has not reached the
 * list yet (a deep link before the first list load): the snapshot is then
 * retaken until it has, rather than settling on "nothing unread".
 */
export interface UnreadAnchorSnapshot {
  anchor: UnreadAnchor | null;
  frozen: boolean;
}

export function snapshotUnreadAnchor(
  chatId: string | null,
  chats: readonly Chat[],
  openAt: OpenConversationAt,
): UnreadAnchorSnapshot {
  if (!chatId || openAt !== 'oldestUnread') return { anchor: null, frozen: true };
  const chat = chats.find((candidate) => candidate.id === chatId);
  if (!chat) return { anchor: null, frozen: false };
  const unreadCount = chat.unreadCount ?? 0;
  if (unreadCount <= 0) return { anchor: null, frozen: true };
  return {
    anchor: {
      chatId: chat.id,
      lastReadAt: chat.last_read_at ?? null,
      unreadCount,
      // Realtime moves lastMessage but not the list route's last_message_id.
      lastMessageId: chat.lastMessage?.id ?? chat.last_message_id ?? null,
    },
    frozen: true,
  };
}

const sameUser = (a: string | null | undefined, b: string): boolean =>
  (a ?? '').toLowerCase() === b.toLowerCase();

// Mirrors get_chat_unread_counts: only other people's live messages count.
const countsAsUnread = (message: Message, currentUserId: string): boolean =>
  !sameUser(message.sender_id, currentUserId) && !message.deleted_at;

export interface FirstUnreadLocation {
  /** Oldest unread among the loaded messages, or null when none of them is unread. */
  id: string | null;
  /**
   * True when the loaded messages reach back past the read cursor, or hold
   * the whole unread count. Until then an older page may hold an earlier
   * unread message, so `id` is provisional.
   */
  covered: boolean;
}

/**
 * Locates the oldest unread message in `messages` (oldest first). Prefers the
 * read cursor; falls back to counting back `unreadCount` messages from the
 * newest, which is also what a "mark as unread" chat needs since its cursor
 * is already current.
 */
export function locateFirstUnread(
  messages: readonly Message[],
  anchor: Pick<UnreadAnchor, 'lastReadAt' | 'unreadCount'>,
  currentUserId: string,
): FirstUnreadLocation {
  if (!currentUserId || messages.length === 0) return { id: null, covered: false };

  const cursor = anchor.lastReadAt ? Date.parse(anchor.lastReadAt) : NaN;
  if (Number.isFinite(cursor)) {
    const firstAfterCursor = messages.find((message) =>
      countsAsUnread(message, currentUserId) && Date.parse(message.created_at) > cursor,
    );
    if (firstAfterCursor) {
      return { id: firstAfterCursor.id, covered: Date.parse(messages[0].created_at) <= cursor };
    }
  }

  if (anchor.unreadCount <= 0) return { id: null, covered: true };
  let remaining = anchor.unreadCount;
  let oldestSeen: string | null = null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!countsAsUnread(message, currentUserId)) continue;
    oldestSeen = message.id;
    remaining -= 1;
    if (remaining === 0) break;
  }
  return { id: oldestSeen, covered: remaining === 0 };
}

/** Convenience wrapper for callers that only need the id. */
export function findFirstUnreadMessageId(
  messages: readonly Message[],
  anchor: Pick<UnreadAnchor, 'lastReadAt' | 'unreadCount'>,
  currentUserId: string,
): string | null {
  return locateFirstUnread(messages, anchor, currentUserId).id;
}

/** True once the loaded page includes the newest message the chat list reported. */
export function includesLatestMessage(
  messages: readonly Message[],
  lastMessageId: string | null,
): boolean {
  if (!lastMessageId) return true;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].id === lastMessageId) return true;
  }
  return false;
}

/**
 * Replies to a task card are folded into the card rather than drawn inline,
 * so a divider placed on one must sit above its card instead.
 */
export function visibleMessageIdFor(messages: readonly Message[], id: string): string {
  const message = messages.find((candidate) => candidate.id === id);
  const parentId = message?.parent_message_id;
  if (!parentId) return id;
  const parent = messages.find((candidate) => candidate.id === parentId);
  return parent?.message_type === 'task_card' ? parent.id : id;
}

/**
 * The read cursor to keep locally after marking a chat read, so reopening it
 * before the next list refresh does not treat read messages as unread. Uses
 * the later of the device clock and the newest known message, since the
 * server stamps its own time and the device clock may run behind it.
 */
export function advancedReadCursor(chat: Chat, nowIso: string): string {
  const candidates = [nowIso, chat.last_read_at, chat.lastMessage?.created_at, chat.last_message_at];
  let latest = nowIso;
  let latestMs = Date.parse(nowIso);
  for (const candidate of candidates) {
    const ms = candidate ? Date.parse(candidate) : NaN;
    if (Number.isFinite(ms) && ms > latestMs) {
      latest = candidate as string;
      latestMs = ms;
    }
  }
  return latest;
}
